"use client";

import { lazy, Suspense, useState, type ReactNode } from "react";
import { ArrowLeft, LogIn, RefreshCcw } from "@/components/icons";
import { ErrorBoundary } from "@/components/error/error-boundary";
import { CopyDiagnosticsButton } from "@/components/error/copy-diagnostics-button";
import { Button } from "@/components/ui/button";
import { signInAgain } from "@/lib/auth-recovery";
import { resolveHandledError } from "@/lib/handle-error";

const ReportIssueForm = lazy(() =>
  import("@/components/feedback/report-issue-form").then((module) => ({
    default: module.ReportIssueForm,
  })),
);

function ReportFormLoadFailure() {
  return (
    <div className="space-y-2 text-center">
      <p className="text-sm text-muted-foreground">
        The report form couldn&apos;t load.
      </p>
      <Button
        variant="outline"
        size="sm"
        onClick={() => window.location.reload()}
      >
        Refresh page
      </Button>
    </div>
  );
}

interface ErrorReportActionsProps {
  error: Error & { digest?: string };
  reset: () => void;
  navButton: ReactNode;
  reportTitlePrefix?: string;
  reportable?: boolean;
}

function ErrorFooter({
  error,
  onReport,
}: {
  error: Error & { digest?: string };
  onReport?: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 border-t border-border pt-3 text-xs text-muted-foreground">
      {onReport && (
        <Button
          type="button"
          variant="link"
          size="bare-xs"
          onClick={onReport}
        >
          Report problem
        </Button>
      )}
      <a
        href="https://status.repackify.com"
        target="_blank"
        rel="noopener noreferrer"
        className="underline underline-offset-2 hover:text-foreground"
      >
        System status
      </a>
      <span className="inline-flex items-center gap-1">
        {error.digest && (
          <span>
            Ref <code className="font-mono">{error.digest}</code>
          </span>
        )}
        <CopyDiagnosticsButton error={error} />
      </span>
    </div>
  );
}

function ReportIssuePanel({
  error,
  reportTitlePrefix,
  onBack,
}: {
  error: Error & { digest?: string };
  reportTitlePrefix: string;
  onBack: () => void;
}) {
  return (
    <div className="space-y-3 text-left">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-foreground">Report problem</p>
        <Button
          type="button"
          onClick={onBack}
          variant="ghost"
          size="compact"
        >
          <ArrowLeft className="size-3.5" />
          Back
        </Button>
      </div>
      <ErrorBoundary label="error-report-form" fallback={<ReportFormLoadFailure />}>
        <Suspense
          fallback={
            <p role="status" className="text-sm text-muted-foreground">
              Loading the report form…
            </p>
          }
        >
          <ReportIssueForm
            variant="inline"
            heading=""
            context={{
              errorDigest: error.digest,
              errorMessage: error.message,
              errorStack: error.stack,
              titlePrefix: reportTitlePrefix,
            }}
          />
        </Suspense>
      </ErrorBoundary>
    </div>
  );
}

export function ErrorReportActions({
  error,
  reset,
  navButton,
  reportTitlePrefix = "Error report",
  reportable = true,
}: ErrorReportActionsProps) {
  const [mode, setMode] = useState<"actions" | "report">("actions");

  if (mode === "report") {
    return (
      <ReportIssuePanel
        error={error}
        reportTitlePrefix={reportTitlePrefix}
        onBack={() => setMode("actions")}
      />
    );
  }

  const { recovery } = resolveHandledError(error);

  return (
    <div className="space-y-2">
      {recovery === "retry" && (
        <Button variant="primary" onClick={() => reset()} className="w-full">
          <RefreshCcw className="size-4" />
          Try again
        </Button>
      )}
      {recovery === "sign-in" && (
        <Button variant="primary" onClick={signInAgain} className="w-full">
          <LogIn className="size-4" />
          Sign in
        </Button>
      )}
      {navButton}
      <div className="pt-3">
        <ErrorFooter
          error={error}
          onReport={reportable ? () => setMode("report") : undefined}
        />
      </div>
    </div>
  );
}
